/**
 * Module dependencies.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Input } from 'app/components/ui/input';
import { cn } from 'app/lib/utils';
import { formatInCurrency } from '../format';
import { DualMoney } from './dual-money';

/**
 * Payment frequency.
 */

type Frequency = 'month' | 'week';

/**
 * Safety cap on the schedule length (≈ 10 years of weekly payments).
 */

const MAX_PAYMENTS = 520;

/**
 * A movement that already happened on the line (shown ahead of the projection).
 * `eur` is what was received/paid in euros when known; amounts otherwise USD.
 */

export type LoanHistoryRow = {
  key: string;
  date: string;
  kind: 'withdrawal' | 'repayment';
  eur: number | null;
  usd: number;
  interest: number;
  principal: number;
  remaining: number;
};

/**
 * One scheduled payment.
 */

type ScheduleRow = {
  index: number;
  date: moment.Moment;
  payment: number;
  interest: number;
  principal: number;
  remaining: number;
};

/**
 * Simulate paying `balance` (USD) off with a fixed payment (USD) every period.
 * Interest accrues daily at `apr` on the remaining balance (as Nexo does) and is
 * settled first by each payment; the rest reduces principal. The last payment
 * only covers what's left. Returns null when the payment can't even cover the
 * interest (the debt would never be repaid).
 */

function simulate(
  balance: number,
  apr: number,
  payment: number,
  frequency: Frequency
): { rows: ScheduleRow[]; capped: boolean } | null {
  const rows: ScheduleRow[] = [];
  let remaining = balance;
  let date = moment().startOf('day');

  while (remaining > 0.005 && rows.length < MAX_PAYMENTS) {
    const next = date.clone().add(1, frequency);
    const days = next.diff(date, 'days');
    const interest = (remaining * (apr / 100) * days) / 365;

    if (payment <= interest) {
      return null;
    }

    const paid = Math.min(payment, remaining + interest);
    const principal = paid - interest;

    remaining = Math.max(remaining - principal, 0);
    rows.push({ index: rows.length + 1, date: next, payment: paid, interest, principal, remaining });
    date = next;
  }

  return { rows, capped: remaining > 0.005 };
}

/**
 * Export `LoanCalculator` component.
 *
 * Payoff simulator for a credit line: starting from the outstanding balance and
 * the current rate (both editable), a fixed payment in EUR every month or week
 * yields the payment schedule, the payoff date and the total interest.
 */

export function LoanCalculator({
  balanceUsd,
  apr,
  history,
  usdPerEur
}: {
  balanceUsd: number;
  apr: number | null;
  history: LoanHistoryRow[];
  usdPerEur: number;
}): JSX.Element {
  const [translate] = useTranslation();
  const [balanceInput, setBalanceInput] = useState(balanceUsd.toFixed(2));
  const [rateInput, setRateInput] = useState(apr != null ? apr.toFixed(1) : '');
  const [paymentInput, setPaymentInput] = useState('');
  const [frequency, setFrequency] = useState<Frequency>('month');

  // Follow new defaults when the data (re)loads or the credit line changes.
  useEffect(() => setBalanceInput(balanceUsd.toFixed(2)), [balanceUsd]);
  useEffect(() => setRateInput(apr != null ? apr.toFixed(1) : ''), [apr]);

  const balance = parseFloat(balanceInput) || 0;
  const rate = parseFloat(rateInput) || 0;
  const paymentEur = parseFloat(paymentInput) || 0;
  const paymentUsd = paymentEur * usdPerEur;
  const eur = (usd: number) => (usdPerEur > 0 ? usd / usdPerEur : 0);

  const result = useMemo(
    () => (balance > 0 && paymentUsd > 0 ? simulate(balance, rate, paymentUsd, frequency) : undefined),
    [balance, rate, paymentUsd, frequency]
  );

  const projectedInterest = result ? result.rows.reduce((sum, row) => sum + row.interest, 0) : 0;
  const projectedPaid = result ? result.rows.reduce((sum, row) => sum + row.payment, 0) : 0;

  // Already paid: the repayments made so far (in € when known, else converted).
  const done = history.filter(row => row.kind === 'repayment');
  const paidInterest = done.reduce((sum, row) => sum + row.interest, 0);
  const paidUsd = done.reduce((sum, row) => sum + row.usd, 0);
  const paidEur = done.reduce((sum, row) => sum + (row.eur ?? eur(row.usd)), 0);

  const totalInterest = paidInterest + projectedInterest;
  const totalPaid = paidUsd + projectedPaid;
  const totalPaidEur = paidEur + eur(projectedPaid);
  const split = (paid: string, projected: string) =>
    translate('cryptoGest.loans.calculator.split', { paid, projected });
  const both = (euros: number, usd: number) => `${formatInCurrency(euros, 'EUR')} / ${formatInCurrency(usd, 'USD')}`;
  const last = result?.rows[result.rows.length - 1];
  const minimumEur = eur((balance * (rate / 100) * (frequency === 'month' ? 31 : 7)) / 365);

  const field = (label: string, input: React.ReactNode, hint?: string) => (
    <div className={'space-y-1'}>
      <label className={'text-xs font-medium text-muted-foreground'}>{label}</label>
      {input}
      {hint && <div className={'text-[11px] text-muted-foreground'}>{hint}</div>}
    </div>
  );

  return (
    <div className={'space-y-4 p-4'}>
      <div className={'grid gap-3 sm:grid-cols-2 lg:grid-cols-4'}>
        {field(
          translate('cryptoGest.loans.calculator.balance'),
          <Input
            inputMode={'decimal'}
            onChange={event => setBalanceInput(event.target.value)}
            type={'number'}
            value={balanceInput}
          />,
          `≈ ${formatInCurrency(eur(balance), 'EUR')}`
        )}

        {field(
          translate('cryptoGest.loans.calculator.rate'),
          <Input
            inputMode={'decimal'}
            onChange={event => setRateInput(event.target.value)}
            type={'number'}
            value={rateInput}
          />,
          translate('cryptoGest.loans.calculator.rateHint')
        )}

        {field(
          translate('cryptoGest.loans.calculator.payment'),
          <Input
            inputMode={'decimal'}
            onChange={event => setPaymentInput(event.target.value)}
            placeholder={'150'}
            type={'number'}
            value={paymentInput}
          />,
          paymentEur > 0 ? `≈ ${formatInCurrency(paymentUsd, 'USD')}` : undefined
        )}

        {field(
          translate('cryptoGest.loans.calculator.frequency'),
          <div className={'inline-flex h-10 w-full rounded-md border border-input bg-background p-0.5 text-sm'}>
            {(['month', 'week'] as Frequency[]).map(option => (
              <button
                className={cn(
                  'flex-1 rounded-[5px] font-medium transition-colors',
                  option === frequency
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                key={option}
                onClick={() => setFrequency(option)}
                type={'button'}
              >
                {translate(`cryptoGest.loans.calculator.${option}`)}
              </button>
            ))}
          </div>
        )}
      </div>

      {result === undefined && (
        <p className={'text-sm text-muted-foreground'}>{translate('cryptoGest.loans.calculator.prompt')}</p>
      )}

      {result === null && (
        <p className={'rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive'}>
          {translate('cryptoGest.loans.calculator.neverPaid', { value: both(minimumEur, minimumEur * usdPerEur) })}
        </p>
      )}

      {result && last && (
        <>
          <div className={'grid gap-3 sm:grid-cols-2 lg:grid-cols-4'}>
            {[
              {
                label: translate('cryptoGest.loans.calculator.payments'),
                value: String(done.length + result.rows.length),
                usd: null,
                sub: split(String(done.length), String(result.rows.length))
              },
              {
                label: translate('cryptoGest.loans.calculator.payoff'),
                value: last.date.format('DD MMM YYYY'),
                usd: null,
                sub: last.date.fromNow()
              },
              {
                label: translate('cryptoGest.loans.calculator.totalInterest'),
                value: formatInCurrency(eur(totalInterest), 'EUR'),
                usd: formatInCurrency(totalInterest, 'USD'),
                sub: split(both(eur(paidInterest), paidInterest), both(eur(projectedInterest), projectedInterest))
              },
              {
                label: translate('cryptoGest.loans.calculator.totalPaid'),
                value: formatInCurrency(totalPaidEur, 'EUR'),
                usd: formatInCurrency(totalPaid, 'USD'),
                sub: split(both(paidEur, paidUsd), both(eur(projectedPaid), projectedPaid))
              }
            ].map(card => (
              <div className={'rounded-lg border border-border bg-background p-3'} key={card.label}>
                <div className={'text-[11px] font-medium uppercase tracking-wide text-muted-foreground'}>
                  {card.label}
                </div>
                <div className={'mt-0.5 text-lg font-semibold tabular-nums text-foreground'}>
                  {card.value}
                  {card.usd && (
                    <span className={'ml-1.5 text-sm font-medium text-muted-foreground'}>{`/ ${card.usd}`}</span>
                  )}
                </div>
                <div className={'text-xs tabular-nums text-muted-foreground'}>{card.sub}</div>
              </div>
            ))}
          </div>

          {result.capped && (
            <p className={'text-sm text-amber-700'}>
              {translate('cryptoGest.loans.calculator.capped', { count: MAX_PAYMENTS })}
            </p>
          )}

          <div className={'max-h-96 overflow-auto rounded-lg border border-border'}>
            <table className={'w-full text-sm'}>
              <thead className={'sticky top-0 bg-card'}>
                <tr className={'border-b border-border text-left text-xs text-muted-foreground'}>
                  <th className={'px-3 py-2 font-medium'}>{'#'}</th>
                  <th className={'px-3 py-2 font-medium'}>{translate('cryptoGest.loans.date')}</th>
                  <th className={'px-3 py-2 text-right font-medium'}>
                    {translate('cryptoGest.loans.calculator.paymentCol')}
                  </th>
                  <th className={'px-3 py-2 text-right font-medium'}>{translate('cryptoGest.loans.interestCol')}</th>
                  <th className={'px-3 py-2 text-right font-medium'}>{translate('cryptoGest.loans.principal')}</th>
                  <th className={'px-3 py-2 text-right font-medium'}>
                    {translate('cryptoGest.loans.calculator.remaining')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.map(row => (
                  <tr className={'border-b border-border bg-muted/30 text-muted-foreground'} key={row.key}>
                    <td className={'px-3 py-1.5'}>
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
                          row.kind === 'withdrawal'
                            ? 'bg-amber-500/15 text-amber-700'
                            : 'bg-emerald-500/15 text-emerald-700'
                        )}
                      >
                        {translate(`cryptoGest.loans.calculator.${row.kind === 'withdrawal' ? 'loan' : 'paid'}`)}
                      </span>
                    </td>
                    <td className={'px-3 py-1.5 tabular-nums'}>{moment(row.date).format('DD MMM YYYY')}</td>
                    <td className={'px-3 py-1.5'}>
                      <DualMoney eur={row.eur} sign={row.kind === 'withdrawal' ? '+' : ''} usd={row.usd} />
                    </td>
                    <td className={'px-3 py-1.5 text-right'}>
                      {row.interest > 0 ? <DualMoney usd={row.interest} /> : '—'}
                    </td>
                    <td className={'px-3 py-1.5'}>
                      <DualMoney sign={row.kind === 'withdrawal' ? '+' : '−'} usd={row.principal} />
                    </td>
                    <td className={'px-3 py-1.5'}>
                      <DualMoney usd={row.remaining} />
                    </td>
                  </tr>
                ))}

                {result.rows.map(row => (
                  <tr className={'border-b border-border last:border-0'} key={row.index}>
                    <td className={'px-3 py-1.5 tabular-nums text-muted-foreground'}>{done.length + row.index}</td>
                    <td className={'px-3 py-1.5 tabular-nums'}>{row.date.format('DD MMM YYYY')}</td>
                    <td className={'px-3 py-1.5'}>
                      <DualMoney usd={row.payment} />
                    </td>
                    <td className={'px-3 py-1.5 text-muted-foreground'}>
                      <DualMoney usd={row.interest} />
                    </td>
                    <td className={'px-3 py-1.5'}>
                      <DualMoney usd={row.principal} />
                    </td>
                    <td className={'px-3 py-1.5 font-medium'}>
                      <DualMoney usd={row.remaining} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className={'sticky bottom-0 bg-card'}>
                <tr className={'border-t border-border font-semibold'}>
                  <td className={'px-3 py-2'} colSpan={2}>
                    {translate('cryptoGest.loans.total')}
                  </td>
                  <td className={'px-3 py-2'}>
                    <DualMoney eur={totalPaidEur} usd={totalPaid} />
                  </td>
                  <td className={'px-3 py-2'}>
                    <DualMoney usd={totalInterest} />
                  </td>
                  <td className={'px-3 py-2'}>
                    <DualMoney usd={totalPaid - totalInterest} />
                  </td>
                  <td className={'px-3 py-2'} />
                </tr>
              </tfoot>
            </table>
          </div>

          <p className={'text-[11px] text-muted-foreground'}>{translate('cryptoGest.loans.calculator.assumptions')}</p>
        </>
      )}
    </div>
  );
}
